import { EventEmitter, Injectable } from '@angular/core';
import { combineLatest, firstValueFrom, from, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { OrderRepository } from '../infrastructure/repositories/order.repository';
import { MembershipState } from '../model/enums/membership-state';
import { OrderChangedState } from '../model/enums/order-changed-state';
import { OrderState } from '../model/enums/order-state';
import { PaymentMethodType } from '../model/enums/payment-method-type';
import { PaymentMethodLabel, PaymentTypeLabel } from '../model/enums/payment-type-label';
import { StorageKeys } from '../model/enums/storage-keys';
import { Order } from '../model/interfaces/order';
import { OrderPurchase } from '../model/interfaces/order-purchase';
import { OrderUpdateAction, OrderUpdateActionType } from '../model/interfaces/order-update-action';
import { Share } from '../model/interfaces/share';
import { ShareType } from '../model/interfaces/share-type';
import { TicketInstance } from '../model/interfaces/ticket-instance';
import { TicketPackage } from '../model/interfaces/ticket-package';
import { TicketPackageInstance } from '../model/interfaces/ticket-package-instance';
import { ClientService } from './client.service';
import { LotteryService } from './lottery.service';
import { PlayerService } from './player.service';
import { PriceService } from './price.service';
import { StorageService } from './storage.service';
import { TrackingFacadeService } from './tracking/tracking-facade.service';
import { TrackingProduct, PromotionProps } from './tracking/tealium.utils';


declare interface eventBusInteface {
    dispatchEvent(event: CustomEvent): void;
}
declare var myEventBus: eventBusInteface;

@Injectable()
export class OrderService {
   public static readonly CONFIG = {
    ORDER_RETRY_ATTEMPTS: 2,
    RETRY_DELAY_BASE_MS: 1000
  } as const;

  private readonly orderRepository: OrderRepository;
  private readonly storageService: StorageService;
  private readonly lotteryService: LotteryService;
  private readonly priceService: PriceService;
  private readonly playerService: PlayerService;
  private readonly trackingFacadeService: TrackingFacadeService;
  private readonly clientService: ClientService;

  private order: Order;
  private orderPurchases: OrderPurchase[] = [];
  private dreamTicketsAdded = new Map<string, Share>();
  private orderSustainSubscription: Subscription;

  // Promise to track ongoing getOrder operations
  private getOrderPromise: Promise<Order | null> | null = null;

  /** Short-lived cache for getOrderById to deduplicate rapid-fire calls */
  private readonly orderByIdCache = new Map<number, { observable: Observable<Order>; expiry: number }>();
  private static readonly ORDER_BY_ID_CACHE_MS = 3000;

  private orderExpirationWarningHandle: any;
  private orderExpirationHandle: any;

  public shareDeleted = new EventEmitter();
  public onOrderContentUpdated = new EventEmitter<{ order: Order; action?: OrderUpdateAction }>();
  public onOrderExpired = new EventEmitter<Order>();
  public onOrderExpirationNotice = new EventEmitter();
  public onOrderFull = new EventEmitter();
  public onOrderCompleted = new EventEmitter();
  public onOrderUpdateIntent = new EventEmitter();
  public onOrderAddIntent = new EventEmitter();
  public onOrderRemovedIntent = new EventEmitter();
  public onOrderChangedState = new EventEmitter<OrderChangedState>();

  public orderIdSubject: Subject<number> = new Subject<number>();

  public constructor(
    orderRepository: OrderRepository,
    storageService: StorageService,
    lotteryService: LotteryService,
    priceService: PriceService,
    playerService: PlayerService,
    trackingFacadeService: TrackingFacadeService,
    clientService: ClientService,
  ) {
    this.storageService = storageService;
    this.orderRepository = orderRepository;
    this.lotteryService = lotteryService;
    this.priceService = priceService;
    this.playerService = playerService;
    this.trackingFacadeService = trackingFacadeService;
    this.clientService = clientService;

    // listen for player-logout in order to invalidate order
    this.playerService.onPlayerLoggedOutNotification.subscribe((options) => {
      if (!options.preserveOrder) {
        this.invalidateOrder();
      }
    });
  }

  /**
   * Adds shares of the provided type by the given count to a representation of an order.
   * @param shareType an instance of the type of share to add to the order.
   * @param count the number of shares to add to the order.
   */
  public async addShareTypeToOrder(shareType: ShareType, count: number): Promise<Order> {
    this.onBeforeOrderUpdate();
    this.onBeforeOrderAdd();

    const order = await this.getOrder(true);
    const shareTypePrice = await firstValueFrom(this.priceService
      .getPriceByShareType(shareType)
      .pipe(take(1)));
    const isFull = await this.checkOrderFull(shareTypePrice.subscriptionSingleMonth);

    if (isFull) {
      return;
    }
    const lotteryClass = await firstValueFrom(this.lotteryService
      .getLotteryClassById(order.SubscriptionStartClassId)
      .pipe(take(1)));

    const updatedOrder = await firstValueFrom(this.orderRepository
      .addShareType(order, shareType, lotteryClass, count)
      .pipe(take(1)));

    this.orderUpdated(
      updatedOrder,
      { actionType: 'add', itemType: 'sharetype', item: shareType }
    );

    ////////////////////////////////////////////////////
    /////////////////// Lod AddToCart //////////////////
    ////////////////////////////////////////////////////
    
    const product: TrackingProduct = {
      id: shareType.Id,
      name: 'Klasselotteriet enkeltlodder ' + shareType.Name,
      quantity: count ? count : 1,
      rows: 1,
      type: 'Enkeltlodder',
      unit_price:
        order.NumberOfClasses === 1
          ? shareTypePrice.subscriptionSingleMonth / 100
          : shareTypePrice.subscriptionSixMonths / 100,
      variant: shareType.Name,
    } as TrackingProduct;
    
    const priceData = await firstValueFrom(this.priceService.getPriceByOrder(updatedOrder).pipe(take(1)));
    this.trackingFacadeService.addToCart([product], this.basketItemCount(updatedOrder), this.basketRowCount(updatedOrder), priceData.totalPrice / 100);

    return updatedOrder;
  }

  /**
 * Persists the customer's subscription billing interval on the current order
 * by updating `Order.NumberOfClasses`.
 *
 * @param count Number of lottery classes (months) per billing cycle:
 * - `1` = monthly plan (charged every month)
 * - `6` = half-year plan (charged every 6 months / one full lottery series)
 *
 * HTTP failures are swallowed silently to preserve the original fire-and-forget
 * behavior; callers should not rely on this method to surface errors.
 */
  public async setClassCount(count: 1 | 6): Promise<void> {
    if (this.order == null) {
      return;
    }

    this.order.NumberOfClasses = count;
    try {
      const updatedOrder = await firstValueFrom(
        this.orderRepository.updateOrder(this.order).pipe(take(1))
      );
      this.orderUpdated(
        updatedOrder,
        { actionType: 'update', item: updatedOrder, itemType: 'order' }
      );
    } catch {
      // Preserve existing silent-failure behavior of the original fire-and-forget subscribe.
    }
  }

  public isEmpty(order: Order): boolean {
    return (
      order == null ||
      order.Shares.length + order.TicketPackageInstances.length + order.Instances.length === 0
    );
  }

  /**
   * Adds a share to the current order.
   * @param shareType an instance of the type of share to add to the order.
   */
  public async addShareToOrder(share: Share): Promise<Order> {
    this.onBeforeOrderUpdate();
    this.onBeforeOrderAdd();

    const order = await this.getOrder(true);
    const sharePrice = await firstValueFrom(this.priceService
      .getPriceByShareType(share.Type)
      .pipe(take(1)));
    const isFull = await this.checkOrderFull(sharePrice.subscriptionSingleMonth);

    if (isFull) {
      return;
    }

    /// If order already has the share, avoid making additional call to backend.
    if (
      order.Shares.find((s) => {
        return s.Ticket.TicketNumber === share.Ticket.TicketNumber && s.Name === share.Name;
      }) != null
    ) {
      return Promise.resolve(order);
    }

    const lotteryClass = await firstValueFrom(this.lotteryService
      .getLotteryClassById(order.SubscriptionStartClassId)
      .pipe(take(1))
    );
    const updatedOrder = await firstValueFrom(this.orderRepository
      .addShare(order, lotteryClass, share)
      .pipe(take(1))
    );

    this.orderUpdated(
      updatedOrder,
      { actionType: 'add', itemType: 'share', item: share }
    );
    this.dreamTicketsAdded.set(`${share.Ticket.TicketNumber}_${share.Name}`, share);

    ////////////////////////////////////////////////////
    //////// Ønskelod / reserved ticket AddToCart //////
    ////////////////////////////////////////////////////

    const ticketType = this.isDreamTicket(share) ? 'ønskelod ' : 'lod ';

    const product: TrackingProduct = {
      id: share.Type.Id,
      name: 'Klasselotteriet ' + ticketType + share.Type.Name,
      quantity: 1,
      rows: 1,
      type: 'Enkeltlodder',
      unit_price:
        order.NumberOfClasses === 1
          ? sharePrice.subscriptionSingleMonth / 100
          : sharePrice.subscriptionSixMonths / 100,
      variant: share.Type.Name,
    } as TrackingProduct;

    const priceData = await firstValueFrom(this.priceService.getPriceByOrder(updatedOrder).pipe(take(1)));
    this.trackingFacadeService.addToCart([product], this.basketItemCount(updatedOrder), this.basketRowCount(updatedOrder), priceData.totalPrice / 100);

    return updatedOrder;
  }

  /**
   * Adds a package of tickets  to the current order.
   * @param ticketPackage
   * @param count
   */
  public async addPackageToOrder(ticketPackage: TicketPackage, count: number): Promise<Order> {
    this.onBeforeOrderUpdate();
    this.onBeforeOrderAdd();

    const order = await this.getOrder(true);
    const packagePrice = await firstValueFrom(this.priceService
      .getPriceByPackage(ticketPackage)
      .pipe(take(1))
    );
    const isFull = await this.checkOrderFull(packagePrice.subscriptionSingleMonth);

    if (isFull) {
      return;
    }

    const updatedOrder = await firstValueFrom(this.orderRepository
      .addPackage(order, ticketPackage, count)
      .pipe(take(1))
    );

    this.orderUpdated(
      updatedOrder,
      { actionType: 'add', itemType: 'ticketpackage', item: ticketPackage }
    );

    ////////////////////////////////////////////////////
    ///////////////// Lodpakke AddToCart ///////////////
    ////////////////////////////////////////////////////

    const product: TrackingProduct = {
      id: ticketPackage.Id,
      name: 'Klasselotteriet lodpakker ' + ticketPackage.Name,
      quantity: count ? count : 1,
      rows: ticketPackage.TicketPackageContents.reduce((sum, c) => sum + c.NumberOfTickets, 0),
      type: 'Lodpakker',
      unit_price:
        order.NumberOfClasses === 1
          ? packagePrice.subscriptionSingleMonth / 100
          : packagePrice.subscriptionSixMonths / 100,
      variant: ticketPackage.Name,
    };

    const priceData = await firstValueFrom(this.priceService.getPriceByOrder(updatedOrder).pipe(take(1)));
    this.trackingFacadeService.addToCart([product], this.basketItemCount(updatedOrder), this.basketRowCount(updatedOrder), priceData.totalPrice / 100);

    return updatedOrder;
  }

  /**
   * Deletes an item from the order.
   * @param share is the item to delete from the order.
   */
  public async deleteShareFromOrder(share: Share): Promise<void> {
    this.onBeforeOrderUpdate();
    this.onBeforeOrderRemove();

    const order = await this.getOrder(false);

    if (order != null) {
      const updatedOrder = await firstValueFrom(this.orderRepository.deleteShare(order, share).pipe(take(1)));
      this.onShareDeleted(updatedOrder, share);

        ////////////////////////////////////////////////////
        ///////////////// RemoveFromCart ///////////////////
        ////////////////////////////////////////////////////

        const ticketType = this.isDreamTicket(share) ? 'ønskelod ' : 'lod ';

        const product: TrackingProduct = {
          id: share.Type.Id,
          name: 'Klasselotteriet ' + ticketType + share.Type.Name,
          quantity: 1,
          rows: 1,
          type: 'Enkeltlodder',
          unit_price: share.Type.Size * 160,
          variant: share.Type.Name,
        } as TrackingProduct;

        const priceData = await firstValueFrom(this.priceService.getPriceByOrder(updatedOrder).pipe(take(1)));
        this.trackingFacadeService.removeFromCart([product], this.basketItemCount(updatedOrder), this.basketRowCount(updatedOrder), priceData.totalPrice / 100);
    }
  }

  private getDreamTicketName(share: Share): string {
    return `${share.Ticket.TicketNumber}_${share.Name}`;
  }

  public isDreamTicket(share: Share): boolean {
    return this.dreamTicketsAdded.has(this.getDreamTicketName(share));
  }

  public saveDreamTicket(share: Share): void {
    this.dreamTicketsAdded.set(this.getDreamTicketName(share), share);
  }

  public removeDreamTicket(share: Share): void {
    this.dreamTicketsAdded.delete(this.getDreamTicketName(share));
  }

  /**
   * Deletes an item from the order.
   * @param orderItem is the item to delete from the order.
   */
  public async deletePackageFromOrder(orderItem: TicketPackageInstance): Promise<void> {
    this.onBeforeOrderUpdate();
    this.onBeforeOrderRemove();

    const order = await this.getOrder(false);

    if (order != null) {
      const updatedOrder = await firstValueFrom(this.orderRepository.deletePackage(order, orderItem).pipe(take(1)));
      this.orderUpdated(
        updatedOrder,
        { actionType: 'delete', itemType: 'ticketpackageinstance', item: orderItem }
      );

        ////////////////////////////////////////////////////
        ///////////////// RemoveFromCart ///////////////////
        ////////////////////////////////////////////////////
        let totalSharesSize = 0;

        orderItem.Shares.forEach((share) => {
          totalSharesSize = totalSharesSize + share.Type.Size;
        });

        const product: TrackingProduct = {
          id: orderItem.TicketPackage.Id,
          name: 'Klasselotteriet ' + orderItem.TicketPackage.Name,
          quantity: 1,
          rows: orderItem.TicketPackage.TicketPackageContents.reduce((sum, c) => sum + c.NumberOfTickets, 0),
          type: 'Lodpakker',
          unit_price: totalSharesSize * 160,
          variant: orderItem.TicketPackage.Name,
        } as TrackingProduct;

        const priceData = await firstValueFrom(this.priceService.getPriceByOrder(updatedOrder).pipe(take(1)));
        this.trackingFacadeService.removeFromCart([product], this.basketItemCount(updatedOrder), this.basketRowCount(updatedOrder), priceData.totalPrice / 100);
    }
  }

  /**
   * Gets an instance of Order. The current Order.
   * @param createIfNull If set to true, creates the order if one not already present.
   */
  public async getOrder(createIfNull: boolean): Promise<Order> {
    // Return cached order if available
    if (this.order != null) {
      return this.order;
    }

    // If there's already a getOrder operation in progress, wait for it
    if (this.getOrderPromise) {
      const result = await this.getOrderPromise;
      if (result != null) {
        return result;
      }
      // If result is null and createIfNull is true, only start a new retrieval if no promise is in progress
      if (createIfNull && !this.getOrderPromise) {
        return await this.executeGetOrder(true);
      }
      return null;
    }

    // Start a new getOrder operation
    return await this.executeGetOrder(createIfNull);
  }

  /**
   * Executes the actual getOrder logic with promise caching
   */
  private async executeGetOrder(createIfNull: boolean): Promise<Order | null> {
    this.getOrderPromise = this.performOrderRetrieval(createIfNull);
    
    try {
      const order = await this.getOrderPromise;
      this.getOrderPromise = null; // Only clear on success
      return order;
    } catch (error) {
      // Do not clear the promise on error; retain for subsequent awaits
      throw error;
    }
  }

  /**
   * Performs the actual order retrieval logic
   */
  private async performOrderRetrieval(createIfNull: boolean): Promise<Order | null> {
    try {
      const order = await this.retrieveOrCreateOrder(createIfNull);
      
      if (order) {
        this.handleOrderSessionStorage(order);
        this.notifyOrderUpdate(order, createIfNull ? 'create' : 'none');
      } else {
        this.clearOrderSessionStorage();
      }

      return order;
    } catch (error) {
      console.error('Error in getOrder:', error);
      this.invalidateOrder();
      throw error;
    }
  }

  /**
   * Retrieves order from cache or creates a new one if needed
   */
  private async retrieveOrCreateOrder(createIfNull: boolean): Promise<Order | null> {
    const cachedOrderId = this.getSessionStorageOrderId();
    
    // Try to get existing order from cache
    if (cachedOrderId) {
      const existingOrder = await this.getExistingOrder(cachedOrderId);
      if (existingOrder && this.isOrderValid(existingOrder)) {
        return existingOrder;
      }
      // Invalid order found, clear cache
      this.invalidateOrder();
    }

    // Create new order if requested and no valid order exists
    return createIfNull ? await this.createNewOrder() : null;
  }

  /**
   * Gets cached order ID from storage
   */
  private getSessionStorageOrderId(): number | null {
    return this.storageService.getItem<number>(StorageKeys.ShopOrderId);
  }

  /**
   * Retrieves existing order by ID with error handling
   */
  private async getExistingOrder(orderId: number): Promise<Order | null> {
    try {
      return await this.safeGetOrder(orderId, OrderService.CONFIG.ORDER_RETRY_ATTEMPTS);
    } catch (error) {
      console.warn('Failed to retrieve existing order after retries:', error);
      return null;
    }
  }

  /**
   * Checks if an order is valid (not expired or completed)
   */
  private isOrderValid(order: Order): boolean {
    const invalidStates = [OrderState.Expired, OrderState.Completed];
    return order && !invalidStates.includes(order.NetOrderState);
  }

  /**
   * Creates a new order
   */
  private async createNewOrder(): Promise<Order> {
    return await firstValueFrom(this.createOrder().pipe(take(1)));
  }

  /**
   * Handles order session storage logic
   */
  private handleOrderSessionStorage(order: Order): void {
    const storageOrderId = this.getSessionStorageOrderId();
    
    if (!storageOrderId) {
      this.storageService.setItem(StorageKeys.ShopOrderId, order.Id);
      this.storageService.setItem(StorageKeys.TrackableOrderId, order.Id);
      
      // This is only used in tracking.service to make sure we have an order id before tracking on order id.
      this.orderIdSubject.next(order.Id);
    }
  }

  /**
   * Clears order cache from storage
   */
  private clearOrderSessionStorage(): void {
    this.storageService.removeItem(StorageKeys.ShopOrderId);
  }

  /**
   * Notifies about order updates
   */
  private notifyOrderUpdate(order: Order, actionType: OrderUpdateActionType): void {
    this.orderUpdated(
      order,
      { actionType, itemType: null, item: null }
    );
  }

  public updateOrder(order: Order): Observable<Order> {
    return this.orderRepository.updateOrder(order).pipe(
      map((o) => {
        // set ticketCount in sesssion storage (for sitecore)
        // todo count ticket i entire order
        // this.storageService.setItem('basket.tickets.count' , order.Shares.length);
        this.orderUpdated(
          o,
          { actionType: 'update', itemType: null, item: null }
        );
        return o;
      })
    );
  }

  public async initOrderSustainLoop(): Promise<void> {
    if (this.orderSustainSubscription != null && !this.orderSustainSubscription.closed) {
      return;
    }

    const stopPoll = new Subject<boolean>();
    this.orderSustainSubscription = timer(0, 750000)
      .pipe(
        switchMap(() => this.sustainOrder()),
        takeUntil(stopPoll)
      )
      .subscribe((order) => {
        if (!order) {
          return;
        }

        const expired = this.isExpired(order);
        if (expired) {
          stopPoll.next(true);
          this.orderSustainSubscription.unsubscribe();
        }

        this.setOrderExpiration(order);
      });
  }

  private sustainOrder(): Observable<Order> {
    return from(this.getOrder(false)).pipe(
      switchMap((order) => {
        if (!order) {
          return of(null);
        }
        return this.orderRepository.sustainOrderById(order.Id);
      })
    );
  }

  private onBeforeOrderAdd(): void {
    this.onOrderAddIntent.emit();
    if (myEventBus) {
      myEventBus.dispatchEvent(new CustomEvent('basket.add'));
    }
  }

  onBeforeOrderRemove(): void {
    this.onOrderRemovedIntent.emit();
    if (myEventBus) {
      myEventBus.dispatchEvent(new CustomEvent('basket.remove'));
    }
  }

  private onBeforeOrderUpdate(): void {
    this.onOrderUpdateIntent.emit();
  }

  private onShareDeleted(order, orderItem): void {
    this.shareDeleted.emit(orderItem);
    this.orderUpdated(
      order,
      { actionType: 'delete', itemType: 'share', item: orderItem }
    );
  }

  private orderUpdated(
    order: Order,
    action: OrderUpdateAction
  ): void {
    const isExpired = this.setOrderExpiration(order);
    if (isExpired) {
      this.order = null;
      return;
    }

    const basketItems = this.basketItemCount(order);

    if (myEventBus) {
      this.storageService.setItem(StorageKeys.BasketTicketsCount, basketItems);
      myEventBus.dispatchEvent(
        new CustomEvent(StorageKeys.BasketTicketsCount, {
          detail: basketItems,
        })
      );
    }

    this.order = order;
    this.onOrderContentUpdated.emit({ order, action });
  }

  private basketItemCount(order: Order): number {
    return order.Shares.length + order.TicketPackageInstances.length;
  }

  private basketRowCount(order: Order): number {
    const packageRows = order.TicketPackageInstances.reduce((sum, tpi) => sum + tpi.Shares.length, 0);
    return order.Shares.length + packageRows;
  }

  private isExpired(order: Order): boolean {
    return this.getExpirationTime(order) <= 0;
  }

  private getExpirationTime(order: Order): number {
    /**
     * ExpirationCountdown
     * Format: "[-][dd.]hh:MM:ss"
     * Example: "-7.20:52:12" equates to minus 7 days, 20 hours, 52 minutes and 12 seconds.
     * */
    const expirationCountDownParts = order.ExpirationCountdown.split(':');
    const hours = parseInt(expirationCountDownParts[0], 10);
    const minutes = parseInt(expirationCountDownParts[1], 10);
    const seconds = parseInt(expirationCountDownParts[2], 10);

    // We need the absolute value of the amount of milliseconds.
    // Due to hours can be negative 0 (-00) which won't parse as a negative value in the calculations.
    // Subsequently we check for the "minus" character as a string value to do the correct negative/positive calculation.
    let expirationMs = Math.abs((hours * 3600 + minutes * 60 + seconds) * 1000);

    // If timestamp is returned negative, the order has expired.
    // This particular check is a consequence of the format in which the timestamp is returned.
    expirationMs *= expirationCountDownParts[0][0] !== '-' ? 1 : -1;

    return expirationMs;
  }

  private clearExpirationTimers(): void {
    window.clearTimeout(this.orderExpirationWarningHandle);
    window.clearTimeout(this.orderExpirationHandle);
  }

  private setOrderExpiration(order: Order): boolean {
    if (order == null) {
      return true;
    }

    this.clearExpirationTimers();

    const expirationMs = this.getExpirationTime(order);

    // FOR TEST ONLY
    //const expirationMs = 5000;

    if (expirationMs <= 0) {
      this.invalidateOrder();
      this.onOrderExpired.emit(order);
      return true;
    }

    const orderExpirationWarningTime = Math.max(0, expirationMs - 300000);
    this.orderExpirationWarningHandle = setTimeout(() => {
      if (this.order != null) {
        this.onOrderExpirationNotice.emit();
      }
    }, orderExpirationWarningTime);

    this.orderExpirationHandle = setTimeout(() => {
      if (this.order == null) {
        return;
      }
      this.invalidateOrder();
      this.onOrderExpired.emit(order);
    }, expirationMs);

    return false;
  }

  private createOrder(): Observable<Order> {
    return this.orderRepository.getOrder().pipe(
      map((order) => {
        return order;
      })
    );
  }

  public createSmtOrder(smtInstances: TicketInstance[]): Observable<Order> {
    return this.orderRepository.createSmtOrder(smtInstances).pipe(
      map((order) => {
        return order;
      })
    );
  }

  public createSmtOrderByOrderId(orderId: number): Observable<Order> {
    return this.orderRepository.createSmtOrderByOrderId(orderId).pipe(
      map((order) => {
        return order;
      })
    );
  }

  public getOrderById(orderId: number): Observable<Order> {
    const now = Date.now();
    const cached = this.orderByIdCache.get(orderId);

    if (cached && cached.expiry > now) {
      return cached.observable;
    }

    const order$ = this.orderRepository.getOrder(orderId).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.orderByIdCache.set(orderId, {
      observable: order$,
      expiry: now + OrderService.ORDER_BY_ID_CACHE_MS
    });

    return order$;
  }

  public orderCompleted(): void {
    // this.refreshOrder();
    this.invalidateOrder();
    this.onOrderCompleted.emit();
  }

  public fmtOrderCompleted(): void {
    this.storageService.removeItem(StorageKeys.PendingFmtOrderId);
    this.storageService.removeItem(StorageKeys.PaymentResponseOrder);
    this.storageService.removeItem(StorageKeys.UpsaleReservation);
  }

  public smtOrderCompleted(): void {
    this.storageService.removeItem(StorageKeys.SmtSelectedShares);
  }

  public getLastOrderPurchase(): OrderPurchase {
    return this.orderPurchases[this.orderPurchases.length - 1];
  }

  public getOrderPurchaseByOrderId(orderId: number): OrderPurchase {
    return this.orderPurchases.find((o) => o.order.Id === orderId);
  }

  public invalidateOrder(): void {
    this.clearExpirationTimers();

    this.order = null;
    this.getOrderPromise = null; // Clear any ongoing promise
    this.clearOrderSessionStorage();

    this.onOrderChangedState.emit(OrderChangedState.Deleted);
  }

  public async refreshOrder(): Promise<Order> {
    try {
      this.order = null;
      this.getOrderPromise = null; // Clear any ongoing promise
      const order = await this.getOrder(false);
      return (this.order = order);
    } catch (error) {
      console.error('Error refreshing order:', error);
      this.invalidateOrder();
      throw error;
    }
  }

  /**
   * Enhanced order validation with better error handling
   */
  private async validateOrderState(order: Order): Promise<boolean> {
    if (!order) {
      return false;
    }

    // Check for expired or completed states
    const invalidStates = [OrderState.Expired, OrderState.Completed];
    if (invalidStates.includes(order.NetOrderState)) {
      console.warn(`Order ${order.Id} is in invalid state: ${order.NetOrderState}`);
      return false;
    }

    // Additional validation: check expiration time
    if (this.isExpired(order)) {
      console.warn(`Order ${order.Id} has expired based on countdown`);
      return false;
    }

    return true;
  }

  /**
   * Safe order retrieval with retry logic
   */
  private async safeGetOrder(orderId: number, retryCount = 1): Promise<Order | null> {
    for (let attempt = 0; attempt < retryCount; attempt++) {
      try {
        const order = await firstValueFrom(
          this.getOrderById(orderId).pipe(take(1))
        );
        
        if (await this.validateOrderState(order)) {
          return order;
        }
        
        return null;
      } catch (error) {
        console.warn(`Attempt ${attempt + 1} failed to retrieve order ${orderId}:`, error);
        
        if (attempt === retryCount - 1) {
          // Last attempt failed
          throw error;
        }
        
        // Wait before retrying using exponential backoff
        await this.delayWithBackoff(attempt);
      }
    }
    
    return null;
  }

  /**
   * Utility method for delays
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Creates exponential backoff delay for retry attempts
   * The delay grows exponentially with each attempt (2^attempt * base delay).
   * Helping to reduce load on the server and avoid immediate repeated retries.
   * 1x base delay, 2x base delay, 4x base delay, etc.
   * TODO: Consider making it a generic backoff helper for wider usage.
   * @param attempt current attempt number (0-based starts with 0)
   */
  private delayWithBackoff(attempt: number): Promise<void> {
    const delayMs = Math.pow(2, attempt) * OrderService.CONFIG.RETRY_DELAY_BASE_MS;
    return this.delay(delayMs);
  }

  private async checkOrderFull(requestedItemPrice: number): Promise<boolean> {
    const order = await this.getOrder(false);

    if (order == null) {
      return false;
    }

    const priceData = await firstValueFrom(this.priceService.getPriceByOrder(order).pipe(take(1)));
    const isFull = priceData.pricePrMonth + requestedItemPrice >= 300000;

    if (isFull) {
      this.onOrderFull.emit();
    }

    return isFull;
  }

  /**
   * Creates a new order based on existing.
   * E.g. in situations where order has expired, and needs to be refreshed with new expiration timestamp.
   * @param order
   */
  private createFromExisting(order: Order): Order {
    return order;
  }

  public updateSplittestValuePairOnOrder(): void {
    if (this.order == null) {
      return;
    }

    const orderClientInfo = this.clientService.getClientInfo();

    this.orderRepository
      .updateSplittestValuePairOnOrder(
        this.order,
        orderClientInfo.SplittestValuePair,
        orderClientInfo.TrackingChannel
      )
      .subscribe();
  }

  public async registerPaymethodIdByExternalReference(orderId: number, paymentMethodExternalReference: string): Promise<void> {
    return firstValueFrom(this.orderRepository.registerPaymethodIdByExternalReference(orderId, paymentMethodExternalReference).pipe(take(1)));
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////                 ////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////    TRACKING     ////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////                 ////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Tracking events for DataLayer.
   * Has been placed here(orderservice) since its dependent on the order for context.
   * @param order
  */

  ///////////////////////////////////////////////////////    Checkout Events     ////////////////////////////////////////////////////////////////////

  public trackCheckoutOnOrder(order: Order, orderStep: number): void {
    const classCount = order.NumberOfClasses;

    this.priceService
      .getPriceByOrder(order)
      .pipe(
        switchMap((priceData) => {
          const variant = classCount !== 1 
            ? PaymentTypeLabel.SixMonthBetalingsservice 
            : PaymentTypeLabel.OneMonthBetalingsservice;
          const tasks = [];
          const packageTrackingEntities = [];

          order.TicketPackageInstances.forEach((ticketPackageInstance) => {
            tasks.push(
              this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
                tap((p) => {
                  packageTrackingEntities.push({
                    name: ticketPackageInstance.TicketPackage.Name,
                    id: ticketPackageInstance.TicketPackage.Name,
                    price:
                      (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                      100,
                    brand: 'dlo_ddk',
                    category: 'lodpakke', // "Enkeltlodder/Lodpakker/Ønskelodder
                    variant: variant, // 1 måned, 6 måneder,
                    quantity: 1,
                  });
                })
              )
            );
          });

          const shareTrackingEntities = [];
          order.Shares.forEach((share) => {
            const wasDreamTicket = this.isDreamTicket(share);
            tasks.push(
              this.priceService.getPriceByShareType(share.Type).pipe(
                tap((p) => {
                  shareTrackingEntities.push({
                    name: share.Type.Name,
                    id: share.Type.Name, // SKU Pakker eller lodnr.
                    price:
                      (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                      100,
                    brand: 'dlo_ddk',
                    category: wasDreamTicket ? 'ønskelod' : 'lod', // "Enkeltlodder/Lodpakker/Ønskelodder
                    variant: variant, // 1 måned, 6 måneder
                    quantity: 1,
                  });
                })
              )
            );
          });

          return combineLatest(tasks).pipe(
            tap(() => {
              const allProducts = shareTrackingEntities.concat(packageTrackingEntities);

              if (orderStep === 1) {
                this.trackingFacadeService.startCheckout(allProducts);
              }
              if (orderStep === 2) {
                this.trackingFacadeService.checkoutGamePlan(allProducts);
              }
              if (orderStep === 3) {
                this.trackingFacadeService.checkoutGapFunding(allProducts);
              }
            })
          );
        })
      )
      .pipe(take(1))
      .subscribe();
  }

  ///////////////////////////////////////////////////////    Purchase Events     ////////////////////////////////////////////////////////////////////

  /**
   * Resolves the spec `payment_method` label sent on the purchase/receipt tracking events.
   * @returns "Betalingskort", "Mobilepay" or "Betalingsservice"
   */
  private resolvePaymentMethodLabel(paymentMethodType?: PaymentMethodType): PaymentMethodLabel {
    if (paymentMethodType === PaymentMethodType.MobilePay) return PaymentMethodLabel.MobilePay;
    if (paymentMethodType === PaymentMethodType.Card) return PaymentMethodLabel.Card;
    return PaymentMethodLabel.Betalingsservice;
  }

  public trackPurchaseComplete(order: Order, membershipState: MembershipState, paymentMethodType?: PaymentMethodType, promotion?: PromotionProps[]): void {
    this.priceService.getPriceByOrder(order).pipe(switchMap((priceData) => {
        const totalPrice = priceData.totalPrice / 100; // price + feeValue

        const tasks = [];
        const shareTrackingEntities = [];
        const packageTrackingEntities = [];

        order.Shares.forEach((share) => {
          tasks.push(
            this.priceService.getPriceByShareType(share.Type).pipe(
              tap((p) => {
                shareTrackingEntities.push({
                  name: 'Klasselotteriet enkeltlodder ' + share.Type.Name,
                  id: share.Type.Id,
                  price: (order.NumberOfClasses === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) / 100,
                  brand: 'dlo_ddk',
                  category: 'lod', // Lottery product (lod, lodpakke)
                  type: 'Enkeltlodder', // Enkeltlodder/Lodpakker/Ønskelodder
                  variant: share.Type.Name, // Amount/type — e.g. "1/1"
                  quantity: 1,
                  rows: 1, // A single ticket is one row — keep parity with basket/payment-method events
                });
              })
            )
          );
        });

        order.TicketPackageInstances.forEach((ticketPackageInstance) => {
          tasks.push(
            this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
              tap((p) => {
                packageTrackingEntities.push({
                  name: 'Klasselotteriet lodpakker ' + ticketPackageInstance.TicketPackage.Name,
                  id: ticketPackageInstance.TicketPackage.Id,
                  price: (order.NumberOfClasses === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) / 100,
                  brand: 'dlo_ddk',
                  category: 'lodpakke', // Lottery product (lod, lodpakke)
                  type: 'Lodpakker', // Enkeltlodder/Lodpakker/Ønskelodder
                  variant: ticketPackageInstance.TicketPackage.Name, // Amount/type — e.g. "Favoritpakken"
                  quantity: 1,
                  // Number of ticket rows contained in the package — keep parity with basket/payment-method events
                  rows: ticketPackageInstance.TicketPackage.TicketPackageContents.reduce((sum, c) => sum + c.NumberOfTickets, 0),
                });
              })
            )
          );
        });

        if (tasks.length === 0) {
          return of(null);
        }

        return combineLatest(tasks).pipe(
          tap(() => {
            // Cart totals — keep parity with basket/payment-method events
            const cartTotalItems = order.Shares.length + order.TicketPackageInstances.length;
            const packageRows = order.TicketPackageInstances.reduce((sum, tpi) => sum + tpi.Shares.length, 0);
            const cartTotalRows = order.Shares.length + packageRows;

            this.trackingFacadeService.purchaseComplete(
              order.Id,
              totalPrice,
              membershipState,
              [...shareTrackingEntities, ...packageTrackingEntities],
              cartTotalItems,
              cartTotalRows,
              this.resolvePaymentMethodLabel(paymentMethodType),
              promotion
            );
          })
        );

      })
    )
      .pipe(take(1))
      .subscribe();
  }

  public trackGapfundComplete(order: Order, membershipState: MembershipState, paymentMethodType: PaymentMethodType): void 
  {
      this.priceService.getPriceByOrder(order).pipe(switchMap((priceData) => 
      {
        const totalPrice = priceData.totalPrice / 100; // price + feeValue

        const tasks = [];
        const shareTrackingEntities = [];
        const instanceTrackingEntities = [];

        order.Shares.forEach((share) => {
          tasks.push(
            this.priceService.getPriceByShareType(share.Type).pipe(
              tap((p) => {
                shareTrackingEntities.push({
                  name: 'Klasselotteriet enkeltlodder ' + share.Type.Name,
                  id: share.Type.Id,
                  price: (order.NumberOfClasses === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) / 100,
                  brand: 'dlo_ddk',
                  category: 'lod', // Lottery product (lod, lodpakke)
                  type: 'Enkeltlodder', // Enkeltlodder/Lodpakker/Ønskelodder
                  variant: share.Type.Name, // Amount/type — e.g. "1/1"
                  quantity: 1,
                });
              })
            )
          );
        });

        order.Instances.forEach((ticketInstance) => {
          tasks.push(
            this.priceService.getPriceByShareType(ticketInstance.Share.Type).pipe(
              tap((p) => {
                instanceTrackingEntities.push({
                  name: 'Klasselotteriet enkeltlodder ' + ticketInstance.Share.Type.Name,
                  id: ticketInstance.Share.Type.Id,
                  price: (order.NumberOfClasses === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) / 100,
                  brand: 'dlo_ddk',
                  category: 'lod', // Lottery product (lod, lodpakke)
                  type: 'Enkeltlodder', // Enkeltlodder/Lodpakker/Ønskelodder
                  variant: ticketInstance.Share.Type.Name, // Amount/type — e.g. "1/1"
                  quantity: 1,
                });
              })
            )
          );
        });

        return combineLatest(tasks).pipe(
          tap(() => {
            const allProducts = shareTrackingEntities.concat(instanceTrackingEntities);
            this.trackingFacadeService.gapfundComplete(order.Id, totalPrice, membershipState, allProducts, this.resolvePaymentMethodLabel(paymentMethodType));
          })
        );
      })
    )
    .pipe(take(1))
    .subscribe();
  }

  public trackSmtSaleOnOrder(
    order: Order,
    membershipState: MembershipState,
  ): void {
    this.priceService
      .getPriceByOrder(order)
      .pipe(
        switchMap((orderPrice) => {
          const totalPrice = orderPrice.pricePrMonth / 100;
          const variant = 'dankort';
          const packageTrackingEntities = [];
          const shareTrackingEntitiesTasks = order.Instances.map((ticketInstance) => {
            return this.priceService.getPriceByShareType(ticketInstance.Share.Type).pipe(
              map((p) => {
                return {
                  name: ticketInstance.Share.Type.Name,
                  id: ticketInstance.Share.Type.Name, // SKU Pakker eller lodnr.
                  price: p.subscriptionSingleMonth / 100,
                  brand: 'dlo_ddk',
                  category: 'Enkeltlodder', // "Enkeltlodder/Lodpakker/Ønskelodder
                  // 'list': this.isDreamTicket(ticketInstance.Share) ? 'Vælg ønskelodder' : 'Vælg lodder',
                  variant: variant, // 1 måned med kort
                  quantity: 1,
                };
              })
            );
          });

          if (shareTrackingEntitiesTasks.length === 0) {
            return of(null);
          }

          return combineLatest(shareTrackingEntitiesTasks).pipe(
            tap((shareTrackingEntities) => {
              this.trackingFacadeService.gapfundComplete(
                order.Id,
                totalPrice,
                membershipState,
                shareTrackingEntities,
                this.resolvePaymentMethodLabel(PaymentMethodType.Card)
              );
            })
          );
        })
      )
      .pipe(take(1))
      .subscribe();
  }
}
